import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Box, Button, Column, Typography, useDevice, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import FullPageInvoice from '~/components/FullPageInvoice/FullPageInvoice';
import ManagePaymentMethods from '~/components/ManagePaymentMethods/ManagePaymentMethods';
import { redirect, routes } from '~/constants';
import { useStoreActions, useStoreStatic } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { triggerFileDownload } from '~/infra/utils';
import { MoneyModel, invoiceModel, relationshipModel, timeModel } from '~/models';
import StripeElementsProvider from '~/providers/StripeElementsProvider';
import { NotificationType } from '~/types/api/notification.model';
import styles from './AdHocInvoiceApproval.module.scss';
import { useAdHocInvoice } from './hooks/useAdHocInvoice';
import { useApproveAdHocInvoice } from './hooks/useApproveAdHocInvoice';
import { useViewInvoicePdf } from './hooks/useViewInvoicePdf';
import { InvoiceStatusScreen } from './InvoiceStatusScreen/InvoiceStatusScreen';
import { InvoiceSummaryPanel } from './InvoiceSummaryPanel/InvoiceSummaryPanel';
import { MobileVendorHeader } from './MobileVendorHeader/MobileVendorHeader';
import { TermsOfService } from './TermsOfService/TermsOfService';
import { TryAnchorBanner } from './TryAnchorBanner/TryAnchorBanner';
export const GetAdHocInvoiceApprovalTID = (parentTestId)=>getTestIds(parentTestId);
function formatVendorAddress(postalAddress) {
    const parts = compact([
        postalAddress.addressOne,
        postalAddress.addressTwo,
        compact([
            postalAddress.city,
            postalAddress.state,
            postalAddress.postalCode
        ]).join(', ')
    ]);
    return parts.length > 0 ? parts.join('\n') : undefined;
}
export const AdHocInvoiceApproval = (props)=>{
    const { testId } = props;
    const { AdHocInvoiceApprovalTestIds } = useMemo(()=>GetAdHocInvoiceApprovalTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { api, setAuthToken } = useStoreActions();
    const { getState } = useStoreStatic();
    const history = useHistory();
    const location = useLocation();
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const [state, setState] = useState({
        kind: 'loading'
    });
    const [selectedFundingSourceId, setSelectedFundingSourceId] = useState(null);
    const [tosChecked, setTosChecked] = useState(false);
    const [mobileStep, setMobileStep] = useState('summary');
    useEffect(()=>{
        if (!isMobileDevice) return;
        document.body.style.setProperty('--bg-standard', '#ffffff');
        return ()=>{
            document.body.style.removeProperty('--bg-standard');
        };
    }, [
        isMobileDevice
    ]);
    const { approveAdHocInvoice, isLoading: isApproving } = useApproveAdHocInvoice();
    const notificationInvoiceId = state.kind === 'ready' ? state.payload.invoice.id : state.kind === 'voided' || state.kind === 'confirmation' ? state.invoiceId : '';
    const { invoice: scoutInvoice, isLoading: isInvoiceLoading } = useAdHocInvoice(notificationInvoiceId);
    const [showFullPageInvoice, setShowFullPageInvoice] = useState(false);
    const invoiceNumber = scoutInvoice != null ? invoiceModel.formattedNumber(scoutInvoice) : '';
    const { viewInvoicePdf } = useViewInvoicePdf({
        invoiceId: notificationInvoiceId,
        invoiceNumber
    });
    const downloadInvoicePdf = useCallback(async ()=>{
        if (!notificationInvoiceId) return;
        try {
            const url = await api.invoice.downloadPDF(notificationInvoiceId);
            await triggerFileDownload({
                url,
                filename: notificationInvoiceId
            });
        } catch (unused) {
            overlayContext.showErrorToast(t.common.toasts.anUnknownErrorHasOccurred());
        }
    }, [
        api,
        notificationInvoiceId,
        overlayContext,
        t
    ]);
    const relationshipId = scoutInvoice != null ? invoiceModel.relationshipId(scoutInvoice) : '';
    const { relationship, isLoading: isRelationshipLoading } = useRelationship(relationshipId, {
        resolvePaymentMethod: false
    });
    useEffect(()=>{
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get('nid');
        if (notificationId == null) {
            history.replace(routes.noAccessPage);
            return;
        }
        const locationState = location.state;
        const passedPayload = locationState === null || locationState === void 0 ? void 0 : locationState.notificationPayload;
        if (passedPayload == null) {
            history.replace(routes.notification(notificationId));
            return;
        }
        let cancelled = false;
        async function applyPayload(payload) {
            if (payload.type !== NotificationType.Invoice) {
                history.replace(routes.noAccessPage);
                return;
            }
            if (payload.authToken != null) {
                await setAuthToken(payload.authToken);
            } else if (!getState().authToken) {
                history.replace(redirect.getPathAndSearch({
                    returnTo: routes.notification(notificationId)
                }));
                return;
            }
            if (cancelled) return;
            setState({
                kind: 'ready',
                payload
            });
        }
        applyPayload(passedPayload);
        return ()=>{
            cancelled = true;
        };
    }, [
        setAuthToken,
        getState,
        history,
        location.search,
        location.state
    ]);
    if (state.kind === 'loading' || state.kind === 'ready' && (isInvoiceLoading || scoutInvoice == null || isRelationshipLoading || relationship == null)) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Box, {
                testId: AdHocInvoiceApprovalTestIds.LoadingSkeleton(),
                className: styles.skeleton
            })
        });
    }
    const resolvedAmount = state.kind === 'ready' ? MoneyModel.fromDTO(state.payload.invoice.amount) : state.amount;
    const resolvedVendorName = state.kind === 'ready' ? state.payload.invoice.vendorName : state.vendorName;
    const isConfirmationStatus = state.kind === 'confirmation' || state.kind === 'ready' && scoutInvoice != null && (invoiceModel.isProcessing(scoutInvoice) || invoiceModel.isConfirmed(scoutInvoice));
    const isPaidStatus = state.kind === 'ready' && scoutInvoice != null && (invoiceModel.isPaid(scoutInvoice) || invoiceModel.isPaidExternally(scoutInvoice));
    // Catch-all: any non-actionable status (voided, refunded, disputed, stopped, etc.) shows the "no longer active" screen
    const isVoidedStatus = state.kind === 'voided' || state.kind === 'ready' && scoutInvoice != null && !invoiceModel.isPending(scoutInvoice) && !invoiceModel.isRequiresPaymentApproval(scoutInvoice) && !isConfirmationStatus && !isPaidStatus;
    if (isConfirmationStatus) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
                testId: AdHocInvoiceApprovalTestIds.InvoiceStatusScreen(),
                title: t.adHocInvoiceApproval.confirmation.title(),
                subtitle: t.adHocInvoiceApproval.confirmation.subtitle({
                    amount: resolvedAmount.display(),
                    vendorName: resolvedVendorName
                }),
                description: t.adHocInvoiceApproval.confirmation.note(),
                footer: /*#__PURE__*/ _jsx(TryAnchorBanner, {}),
                animate: state.kind === 'confirmation'
            })
        });
    }
    if ((isPaidStatus || isVoidedStatus) && showFullPageInvoice) {
        return /*#__PURE__*/ _jsx(FullPageInvoice, {
            invoiceId: notificationInvoiceId,
            relationshipId: relationshipId,
            dismissModal: ()=>setShowFullPageInvoice(false),
            hideDropdownActions: true
        });
    }
    if (isPaidStatus) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
                testId: AdHocInvoiceApprovalTestIds.InvoiceStatusScreen(),
                title: t.adHocInvoiceApproval.alreadyPaid.title(),
                subtitle: t.adHocInvoiceApproval.alreadyPaid.subtitle({
                    amount: resolvedAmount.display(),
                    vendorName: resolvedVendorName
                }),
                description: t.adHocInvoiceApproval.alreadyPaid.description(),
                onDownloadPdf: downloadInvoicePdf,
                onViewInvoice: ()=>setShowFullPageInvoice(true)
            })
        });
    }
    if (isVoidedStatus) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(InvoiceStatusScreen, {
                testId: AdHocInvoiceApprovalTestIds.InvoiceStatusScreen(),
                title: t.adHocInvoiceApproval.voided.title(),
                subtitle: t.adHocInvoiceApproval.voided.subtitle({
                    amount: resolvedAmount.display(),
                    vendorName: resolvedVendorName
                }),
                onDownloadPdf: downloadInvoicePdf,
                onViewInvoice: ()=>setShowFullPageInvoice(true)
            })
        });
    }
    const { invoice } = state.payload;
    const amount = MoneyModel.fromDTO(invoice.amount);
    const legalTermsDocument = scoutInvoice != null ? scoutInvoice.legalTermsDocument : null;
    const requiresTos = legalTermsDocument != null;
    const vendorId = relationship != null ? relationshipModel.vendor(relationship) : '';
    const customerId = relationship != null ? relationshipModel.customer(relationship) : '';
    const hasPaymentMethodIds = vendorId !== '' && customerId !== '';
    const isApproveDisabled = selectedFundingSourceId == null || requiresTos && !tosChecked;
    const vendorLogoUrl = relationship != null ? relationshipModel.vendorLogoUrl(relationship) : undefined;
    const vendorAddress = relationship != null ? formatVendorAddress(relationship.vendor.postalAddress) : undefined;
    const customerName = scoutInvoice != null ? invoiceModel.customerName(scoutInvoice) : undefined;
    const vendorName = scoutInvoice != null ? invoiceModel.vendorName(scoutInvoice) : invoice.vendorName;
    const issueDate = scoutInvoice != null ? timeModel.formatDateLong(scoutInvoice.rawIssuedOn) : undefined;
    const dueDate = scoutInvoice != null ? timeModel.formatDateLong(scoutInvoice.rawDueDate) : undefined;
    const handleApprove = ()=>{
        if (selectedFundingSourceId == null) return;
        approveAdHocInvoice({
            invoiceId: invoice.id,
            fundingSourceId: selectedFundingSourceId,
            approvedLegalTermsId: legalTermsDocument === null || legalTermsDocument === void 0 ? void 0 : legalTermsDocument.legalTermsId
        }, {
            onSuccess: ()=>setState({
                    kind: 'confirmation',
                    invoiceId: invoice.id,
                    amount,
                    vendorName
                }),
            onError: (error)=>{
                const errorResponse = new ErrorResponse(error);
                if (errorResponse.hasServerError(ServerError.InvoiceAlreadyApproved)) {
                    setState({
                        kind: 'confirmation',
                        invoiceId: invoice.id,
                        amount,
                        vendorName
                    });
                } else if (errorResponse.hasServerError(ServerError.InvoiceWasVoided)) {
                    setState({
                        kind: 'voided',
                        invoiceId: invoice.id,
                        vendorName,
                        amount
                    });
                } else {
                    overlayContext.showErrorToast(t.adHocInvoiceApproval.approveCta.approveError(), AdHocInvoiceApprovalTestIds.ApproveError());
                }
            }
        });
    };
    const approveCta = /*#__PURE__*/ _jsxs(Column, {
        gap: 8,
        children: [
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'approve_ad_hoc_invoice'
                },
                testId: AdHocInvoiceApprovalTestIds.ApproveCta(),
                skin: "primary",
                size: "large",
                fluid: true,
                disabled: isApproveDisabled,
                loading: isApproving,
                onClick: handleApprove,
                children: t.adHocInvoiceApproval.approveCta.approveAndPay({
                    amount: amount.display()
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                weight: "normal",
                className: styles.disclaimer,
                children: t.adHocInvoiceApproval.approveCta.disclaimer()
            }),
            /*#__PURE__*/ _jsx(BankSecurityRow, {
                className: styles.bankSecurity
            })
        ]
    });
    const paymentMethodOptions = /*#__PURE__*/ _jsxs("div", {
        className: styles.rightPanelTop,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                testId: AdHocInvoiceApprovalTestIds.PaymentMethodPanel(),
                variant: "paragraph1",
                weight: "bold",
                className: styles.rightPanelTitle,
                children: t.adHocInvoiceApproval.paymentMethodPanel.title()
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.paymentCard,
                children: hasPaymentMethodIds ? /*#__PURE__*/ _jsx(StripeElementsProvider, {
                    children: /*#__PURE__*/ _jsx(ManagePaymentMethods, {
                        isProposal: true,
                        isAdHoc: true,
                        updateProgress: false,
                        vendorId: vendorId,
                        customerId: customerId,
                        relationshipId: relationshipId,
                        isPreview: false,
                        isSample: false,
                        isMemberClient: false,
                        isInviteMemberDisabled: true,
                        hideInviteMemberHint: true,
                        showCreditCardFee: relationship != null ? !relationshipModel.actualIsVendorCoveringCCFee(relationship) : true,
                        isVendorCoveringFees: relationship != null ? relationshipModel.actualIsVendorCoveringCCFee(relationship) : false,
                        onSelectPaymentMethod: (pm)=>{
                            var _ref;
                            return setSelectedFundingSourceId((_ref = pm === null || pm === void 0 ? void 0 : pm.id) !== null && _ref !== void 0 ? _ref : null);
                        }
                    })
                }) : null
            })
        ]
    });
    const actionFooterContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TermsOfService, {
                testId: AdHocInvoiceApprovalTestIds.TermsOfService(),
                legalTermsDocument: legalTermsDocument,
                checked: tosChecked,
                onCheckedChange: setTosChecked,
                vendorId: vendorId,
                invoiceId: invoice.id
            }),
            approveCta
        ]
    });
    if (isMobileDevice) {
        const mobileHeader = /*#__PURE__*/ _jsx("div", {
            className: styles.mobileHeaderWrapper,
            children: /*#__PURE__*/ _jsx(MobileVendorHeader, {
                vendorName: vendorName,
                vendorLogoUrl: vendorLogoUrl !== null && vendorLogoUrl !== void 0 ? vendorLogoUrl : undefined,
                onBack: mobileStep === 'payment' ? ()=>setMobileStep('summary') : undefined,
                testId: AdHocInvoiceApprovalTestIds.MobilePaymentHeader(),
                backButtonTestId: AdHocInvoiceApprovalTestIds.MobileBackButton()
            })
        });
        if (mobileStep === 'summary') {
            return /*#__PURE__*/ _jsx(Box, {
                testId: AdHocInvoiceApprovalTestIds.Root(),
                className: cn(styles.pageWrapper, styles.pageWrapperMobile),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: cn(styles.page, styles.pageMobile),
                    children: [
                        mobileHeader,
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.leftPanelMobile,
                            children: /*#__PURE__*/ _jsx(InvoiceSummaryPanel, {
                                testId: AdHocInvoiceApprovalTestIds.InvoiceSummaryPanel(),
                                vendorName: vendorName,
                                vendorLogoUrl: vendorLogoUrl !== null && vendorLogoUrl !== void 0 ? vendorLogoUrl : undefined,
                                vendorAddress: vendorAddress,
                                clientName: customerName,
                                invoiceNumber: invoiceNumber,
                                issueDate: issueDate,
                                dueDate: dueDate,
                                amountDue: amount,
                                onViewInvoiceClick: ()=>viewInvoicePdf({
                                        showDownloadButton: true
                                    }),
                                hideVendorBlock: true,
                                mobile: true
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.mobilePayNowSticky,
                            children: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'ad_hoc_invoice_mobile_pay_now'
                                },
                                testId: AdHocInvoiceApprovalTestIds.MobilePayNowSticky(),
                                skin: "primary",
                                size: "large",
                                fluid: true,
                                onClick: ()=>setMobileStep('payment'),
                                children: t.adHocInvoiceApproval.mobile.payNow()
                            })
                        })
                    ]
                })
            });
        }
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            className: cn(styles.pageWrapper, styles.pageWrapperMobile),
            children: /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.page, styles.pageMobile),
                children: [
                    mobileHeader,
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.rightPanel, styles.rightPanelMobile),
                        children: paymentMethodOptions
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        "data-testid": AdHocInvoiceApprovalTestIds.ActionFooter(),
                        className: cn(styles.rightPanelBottom, styles.rightPanelBottomMobile),
                        children: actionFooterContent
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(Box, {
        testId: AdHocInvoiceApprovalTestIds.Root(),
        className: styles.pageWrapper,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.SplitLayout(),
            className: styles.page,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.leftPanel,
                    children: /*#__PURE__*/ _jsx(InvoiceSummaryPanel, {
                        testId: AdHocInvoiceApprovalTestIds.InvoiceSummaryPanel(),
                        vendorName: vendorName,
                        vendorLogoUrl: vendorLogoUrl !== null && vendorLogoUrl !== void 0 ? vendorLogoUrl : undefined,
                        vendorAddress: vendorAddress,
                        clientName: customerName,
                        invoiceNumber: invoiceNumber,
                        issueDate: issueDate,
                        dueDate: dueDate,
                        amountDue: amount,
                        onViewInvoiceClick: ()=>viewInvoicePdf({
                                showDownloadButton: true
                            })
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.rightPanel,
                    children: [
                        paymentMethodOptions,
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": AdHocInvoiceApprovalTestIds.ActionFooter(),
                            className: styles.rightPanelBottom,
                            children: actionFooterContent
                        })
                    ]
                })
            ]
        })
    });
};
